import React from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Row, Col, Button, Radio, Tooltip, Collapse } from 'antd';

import { useEvaluationRisk } from './hooks';
import Textarea from '@/components/ui/Textarea';
import FormItem from '@/components/ui/FormItem';
import { VideoIcon } from '@/assets/icons/video';
import { RiskBadge } from '../../components/RiskBadge';
import { ActionPlan } from '../../components/ActionPlan';
import ButtonDefault from '@/components/ui/ButtonDefault';
import { VideoDrawer } from '../../components/VideoDrawer';
import { ErgonomicTools, ErgonomicToolsReview } from '../../components/ErgonomicTools';
import { ModalDelete } from '@/views/ActionPlans/components/Modals/ModalDelete';
import { TableActionPlanEvaluationRisk } from '../../components/TableActionPlanEvaluationRisk';
import * as S from './styles';

interface EvaluationRiskProps {
	goToSubStep: (step: number) => void;
}

const { Panel } = Collapse;

export function EvaluationRisk({ goToSubStep }: Readonly<EvaluationRiskProps>): JSX.Element {
	const {
		risk,
		category,
		assessment,
		actionPlan,
		isLoading,
		openModalDelete,
		openActionPlan,
		titleActionPlan,
		openVideoDrawer,
		activeKey,
		newReview,
		categoryPrevious,
		previousReview,
		relatedReport,
		ergonomicKeyNames,
		handleOnToggleCollapse,
		handleActionPlan,
		handleOpenModalDelete,
		handleOpenVideoDrawer,
		setOpenModalDelete,
		setOpenVideoDrawer,
		handleUpdateCategoryResult,
		handleAddActionPlan,
		handleOnDeleteActionPlan
	} = useEvaluationRisk({ goToSubStep });

	const ergonomicToolName = ergonomicKeyNames[category?.key as keyof typeof ergonomicKeyNames]?.toUpperCase();

	return (
		<>
			<S.DefaultRowContent gutter={[32, 16]} align="bottom">
				<Col span={24} style={{ marginBottom: '20px' }}>
					<S.DefaultStepHeading>
						{assessment.activity?.name} - {I18n.get(category?.name)}
					</S.DefaultStepHeading>
				</Col>
				<Col span={24} style={{ marginBottom: '20px' }}>
					<S.FlexSpaceBetween>
						<S.DefaultStepTitle>{I18n.get('Risk evaluation')}</S.DefaultStepTitle>
						<ButtonDefault
							type="link"
							style={{ display: 'flex', alignItems: 'self-end' }}
							onClick={handleOpenVideoDrawer}
						>
							<VideoIcon width={30} height={30} />
							{I18n.get('Watch video')}
						</ButtonDefault>
					</S.FlexSpaceBetween>
					<S.DefaultStepSubTitle>
						{I18n.get('Identify tasks and carry out their evaluation.')}
						{category?.key && ergonomicToolName
							? I18n.get('EvaluationErgonomicRisk886').replace(
									'{{ergonomic_tool_name}}',
									ergonomicToolName
							  )
							: ''}
					</S.DefaultStepSubTitle>
				</Col>
				{(newReview || previousReview?.id) && categoryPrevious && (
					<Col span={24} style={{ marginBottom: '20px' }}>
						<S.CustomButtonDefault
							type="default"
							onClick={handleOnToggleCollapse}
							className={activeKey.length ? 'active' : ''}
						>
							{I18n.get('View previous evaluation')}
						</S.CustomButtonDefault>
						<S.CustomCollapse activeKey={activeKey} bordered={false}>
							<Panel key="1" showArrow={false} header={null}>
								<Row gutter={[0, 16]}>
									<S.CustomColFlexStart span={12}>
										<S.CustomLabel>{I18n.get('Risk level')}</S.CustomLabel>
										<RiskBadge risk={categoryPrevious.risk} />
									</S.CustomColFlexStart>
									{categoryPrevious.related_report && (
										<S.CustomColFlexStart span={12}>
											<S.CustomLabel>{I18n.get('Backup tool:')}</S.CustomLabel>
											<ErgonomicToolsReview
												category={categoryPrevious.category.key}
												relatedReport={categoryPrevious.related_report}
											/>
										</S.CustomColFlexStart>
									)}
									<Col
										span={24}
										style={{ flexDirection: 'column', alignItems: 'start', marginBottom: '20px' }}
									>
										<S.CustomLabel>{I18n.get('Observations')}</S.CustomLabel>
										<S.CustomText>{categoryPrevious.observation || '-'}</S.CustomText>
									</Col>
									<Col span={24}>
										<TableActionPlanEvaluationRisk categoryPrevious={categoryPrevious} />
									</Col>
								</Row>
							</Panel>
						</S.CustomCollapse>
					</Col>
				)}
				<Row style={{ width: '100%', marginBottom: '20px' }} gutter={[0, 16]}>
					<Col span={24} xl={12} style={{ padding: '0 30px 0 20px' }}>
						<S.CustomLabel>{I18n.get('Select the risk level:')}</S.CustomLabel>
						<FormItem name={['evaluation_risk', 'risk_level']}>
							<S.CustomRadioGroup defaultValue="TOLERABLE">
								<Radio.Button value="TOLERABLE" className="radio-tolerable" disabled={isLoading}>
									{I18n.get('Tolerable risk')}
								</Radio.Button>
								<Radio.Button value="MODERATE" className="radio-moderate" disabled={isLoading}>
									{I18n.get('Moderate risk')}
								</Radio.Button>
								<Radio.Button value="NOT_TOLERABLE" className="radio-untolerable" disabled={isLoading}>
									{I18n.get('Not tolerable risk')}
								</Radio.Button>
							</S.CustomRadioGroup>
						</FormItem>
					</Col>
					<ErgonomicTools category={category?.key || ''} relatedReport={relatedReport || null} />
				</Row>
				<Row style={{ width: '100%', marginBottom: '40px' }} gutter={[0, 16]}>
					<Col span={24} style={{ padding: '0 20px' }}>
						<FormItem label="Observations:" name={['evaluation_risk', 'observations']}>
							<Textarea
								placeholder={I18n.get('Enter your observations')}
								maxLength={1000}
								rows={5}
								showCount
								style={{ resize: 'none', width: '100%' }}
								disabled={isLoading}
							/>
						</FormItem>
					</Col>
				</Row>
				<Row style={{ width: '100%' }} gutter={[0, 16]}>
					<Col span={24} style={{ padding: '0 20px' }}>
						<S.DefaultStepTitle style={{ marginBottom: '30px' }}>
							{I18n.get('Action plan')}:
							<Tooltip
								title={I18n.get(
									'Action plans must be developed obligatorily for risks classified as moderate and not tolerable.'
								)}
							>
								<S.TooltipIcon />
							</Tooltip>
						</S.DefaultStepTitle>

						{actionPlan && actionPlan.id ? (
							<S.CustomActionPlanCard>
								<h4>{actionPlan.title}</h4>
								<p>{moment(actionPlan.due_date).format('DD/MM/YYYY')}</p>
								<div className="actions">
									<Button
										type="link"
										size="small"
										style={{ display: 'flex', alignItems: 'center', height: 'auto' }}
										onClick={handleAddActionPlan}
										icon={
											<Tooltip title={I18n.get('Edit')}>
												<EditOutlined style={{ color: '#002FBB', fontSize: '20px' }} />
											</Tooltip>
										}
									/>
									<Button
										type="link"
										size="small"
										style={{ display: 'flex', alignItems: 'center', height: 'auto' }}
										onClick={handleOpenModalDelete}
										icon={
											<Tooltip title={I18n.get('Delete')}>
												<DeleteOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
											</Tooltip>
										}
									/>
								</div>
							</S.CustomActionPlanCard>
						) : (
							<ButtonDefault onClick={handleAddActionPlan}>{I18n.get('Add action plan')}</ButtonDefault>
						)}
					</Col>
				</Row>
			</S.DefaultRowContent>
			<Row gutter={[16, 16]} style={{ padding: '30px 0' }}>
				<Col span={24} style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
					<ButtonDefault type="default" onClick={() => goToSubStep(3)} loading={isLoading}>
						{I18n.get('Back')}
					</ButtonDefault>
					<ButtonDefault type="primary" onClick={handleUpdateCategoryResult} loading={isLoading}>
						{I18n.get('Next')}
					</ButtonDefault>
				</Col>
			</Row>
			<ModalDelete
				open={openModalDelete}
				onConfirm={handleOnDeleteActionPlan}
				onCancel={() => setOpenModalDelete(false)}
				description={I18n.get('Are you sure you want to delete the action plan')}
			/>
			<ActionPlan open={openActionPlan} title={titleActionPlan} onClose={handleActionPlan} risk={risk} />
			<VideoDrawer open={openVideoDrawer} onClose={() => setOpenVideoDrawer(false)} />
		</>
	);
}
